import React, { useState } from 'react';
import {
  Box, Typography, Button, Container, Stack, Grid, Chip, IconButton,
  Tooltip, useTheme, useMediaQuery, Paper, Divider
} from '@mui/material';

// MUI Icons
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LoginIcon from '@mui/icons-material/Login';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleIcon from '@mui/icons-material/People';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DescriptionIcon from '@mui/icons-material/Description';
import LayersIcon from '@mui/icons-material/Layers';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GavelIcon from '@mui/icons-material/Gavel';
import ShieldIcon from '@mui/icons-material/Shield';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BusinessIcon from '@mui/icons-material/Business';
import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import LockIcon from '@mui/icons-material/Lock';
import DevicesIcon from '@mui/icons-material/Devices';

export default function LandingPage({ onGoToLogin }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeStep, setActiveStep] = useState(0);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 14 Payroll Workflow Steps
  const workflowSteps = [
    { number: 1, title: 'Payroll Setup', icon: <BusinessIcon />, desc: 'Configure company structures, legal entities, business groups, and legislation rules.' },
    { number: 2, title: 'Pay Period Configuration', icon: <DateRangeIcon />, desc: 'Define active calendar cycles, processing cutoff dates, and payment frequency rules.' },
    { number: 3, title: 'Employee Registration', icon: <PeopleIcon />, desc: 'Maintain complete digital profiles, personal demographics, and legal identification.' },
    { number: 4, title: 'Employee Assignment', icon: <LayersIcon />, desc: 'Map employees to job designations, operational departments, and reporting managers.' },
    { number: 5, title: 'Salary Template Setup', icon: <DescriptionIcon />, desc: 'Build standardized templates with custom earnings, allowances, and deduction math.' },
    { number: 6, title: 'Employee Salary Setup', icon: <AttachMoneyIcon />, desc: 'Assign base packages, recurring allowances, and custom pay revisions to staff.' },
    { number: 7, title: 'Attendance & Leave Sync', icon: <EventNoteIcon />, desc: 'Fetch attendance logs, shift records, leave balances, and unpaid LOP days.' },
    { number: 8, title: 'Tax & PF Registration', icon: <GavelIcon />, desc: 'Register statutory PF accounts, ESI numbers, and employee tax declarations.' },
    { number: 9, title: 'Advance Salary Processing', icon: <AccountBalanceWalletIcon />, desc: 'Manage salary advance requests, approval workflows, and auto-repayment schedules.' },
    { number: 10, title: 'Payroll Run Execution', icon: <TrendingUpIcon />, desc: 'Execute automated payroll engines to process gross-to-net salary math in batch.' },
    { number: 11, title: 'Run Result Review', icon: <AssessmentIcon />, desc: 'Audit calculated gross pay, statutory deductions, net salaries, and variance logs.' },
    { number: 12, title: 'Payment Processing', icon: <CreditCardIcon />, desc: 'Validate disbursement totals, manage payment methods, and finalize run batch.' },
    { number: 13, title: 'Payslip Generation', icon: <ReceiptLongIcon />, desc: 'Automatically generate downloadable digital PDF payslips for all staff.' },
    { number: 14, title: 'Bank Payment File Export', icon: <AccountBalanceIcon />, desc: 'Export formatted bank transfer files for single-click direct bank payouts.' },
  ];

  // Core Modules (Exact 16 requested)
  const coreModules = [
    { name: 'Payroll', icon: <BusinessIcon />, desc: 'Configure core payroll parameters, calculation rules, and pay groups.' },
    { name: 'Pay Period', icon: <DateRangeIcon />, desc: 'Manage monthly pay cycles, processing windows, and cutoff dates.' },
    { name: 'Salary Templates', icon: <DescriptionIcon />, desc: 'Create reusable earnings, allowance, and deduction compensation structures.' },
    { name: 'Person', icon: <PeopleIcon />, desc: 'Centralized employee directory with biographical and contact records.' },
    { name: 'Assignment', icon: <LayersIcon />, desc: 'Job mapping, departmental assignments, and manager hierarchies.' },
    { name: 'Salary', icon: <AttachMoneyIcon />, desc: 'Individual employee compensation setup and revision history.' },
    { name: 'Attendance', icon: <EventNoteIcon />, desc: 'Daily attendance tracking, shift definitions, and work hours.' },
    { name: 'Leave Balance', icon: <EventNoteIcon />, desc: 'Leave quotas, leave balance tracking, and time-off accruals.' },
    { name: 'Tax Declaration', icon: <GavelIcon />, desc: 'Employee IT tax declarations, regime choices, and deduction proofs.' },
    { name: 'PF Account', icon: <ShieldIcon />, desc: 'Provident Fund, ESI, and statutory contribution record keeping.' },
    { name: 'Advance Salary', icon: <AccountBalanceWalletIcon />, desc: 'Salary advance policies, employee requests, and repayment ledgers.' },
    { name: 'Payroll Run', icon: <TrendingUpIcon />, desc: 'Automated calculation engine for computing monthly salaries.' },
    { name: 'Run Result', icon: <AssessmentIcon />, desc: 'Itemized calculation outputs, line items, and audit breakdowns.' },
    { name: 'Payment', icon: <CreditCardIcon />, desc: 'Disbursement management across cash, cheque, and bank transfers.' },
    { name: 'Payslip', icon: <ReceiptLongIcon />, desc: 'PDF payslip generation with detailed earnings and tax breakdowns.' },
    { name: 'Bank Payment File', icon: <AccountBalanceIcon />, desc: 'Formatted bank advice export files for direct salary deposits.' },
  ];

  // Key Features (Exact 8 requested)
  const keyFeatures = [
    { title: 'Automated Payroll Processing', icon: <SpeedIcon />, desc: 'Execute end-to-end salary calculations from gross to net pay in seconds with zero manual errors.' },
    { title: 'Employee Management', icon: <PeopleIcon />, desc: 'Maintain complete digital employee records from initial onboarding to final separation.' },
    { title: 'Salary Templates', icon: <DescriptionIcon />, desc: 'Design flexible compensation templates with custom formulas for base pay, HRA, and allowances.' },
    { title: 'Attendance Integration', icon: <EventNoteIcon />, desc: 'Automatically sync attendance logs and leave balances into payroll loss-of-pay calculations.' },
    { title: 'Tax & PF Management', icon: <GavelIcon />, desc: 'Built-in statutory compliance engine for automated PF, ESI, PT, and TDS tax deductions.' },
    { title: 'Payslip Generation', icon: <ReceiptLongIcon />, desc: 'Generate secure, professional PDF payslips accessible by employees immediately after pay run.' },
    { title: 'Secure Role-Based Access', icon: <SecurityIcon />, desc: 'Granular user permission controls ensuring administrative data security and privacy.' },
    { title: 'Reports & Analytics', icon: <AssessmentIcon />, desc: 'Comprehensive financial reporting, costing summaries, and audit trail analytics.' },
  ];



  // Highlights (6 infographic items - with 1 accent highlight)
  const highlights = [
    { title: 'End-to-End Payroll', value: '100% Automated', desc: 'From onboarding to bank file export', highlight: false },
    { title: 'Multi-Company Support', value: 'Multi-Tenant', desc: 'Manage multiple legal entities easily', highlight: false },
    { title: 'Compliance & Tax Engine', value: 'Zero Errors', desc: 'Automated PF, ESI, PT & TDS rules', highlight: true }, // Accent pink card
    { title: 'Secure Architecture', value: 'Enterprise Grade', desc: 'JWT token auth & sanitized APIs', highlight: false },
    { title: 'Scalable Solution', value: 'High Performance', desc: 'Optimized PostgreSQL & Prisma ORM', highlight: false },
    { title: 'Fast Processing', value: 'Sub-second Math', desc: 'Instant batch salary processing', highlight: false }
  ];

  return (
    <Box sx={{ bgcolor: '#F8FAFC', color: '#0F172A', minHeight: '100vh', fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>
      
      {/* ── Fixed Light Navigation Bar ── */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 1000,
        bgcolor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <Container maxWidth="xl">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1.8 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ cursor: 'pointer' }} onClick={() => scrollToSection('hero')}>
              <Box sx={{
                width: 38, height: 38, borderRadius: '10px',
                background: 'linear-gradient(135deg, #16A34A, #22C55E)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 3px 8px rgba(22, 163, 74, 0.3)'
              }}>
                <Typography fontWeight={900} fontSize={20} color="white">V</Typography>
              </Box>
              <Box>
                <Typography fontSize={17} fontWeight={800} color="#0F172A" sx={{ letterSpacing: -0.4, lineHeight: 1.1 }}>
                  VVSPL HRMS
                </Typography>
                <Typography fontSize={10.5} color="#64748B" sx={{ letterSpacing: 0.5, textTransform: 'uppercase', fontWeight: 600 }}>
                  Payroll System
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={3.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Typography sx={{ color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer', '&:hover': { color: '#16A34A' } }} onClick={() => scrollToSection('what-is-hrms')}>Overview</Typography>
              <Typography sx={{ color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer', '&:hover': { color: '#16A34A' } }} onClick={() => scrollToSection('who-uses-hrms')}>Who Uses It</Typography>
              <Typography sx={{ color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer', '&:hover': { color: '#16A34A' } }} onClick={() => scrollToSection('why-hrms')}>Before vs After</Typography>
              <Typography sx={{ color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer', '&:hover': { color: '#16A34A' } }} onClick={() => scrollToSection('workflow')}>Workflow</Typography>
              <Typography sx={{ color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer', '&:hover': { color: '#16A34A' } }} onClick={() => scrollToSection('modules')}>Modules</Typography>
            </Stack>

            <Button
              variant="contained"
              onClick={onGoToLogin}
              startIcon={<LoginIcon />}
              sx={{
                bgcolor: '#16A34A', color: 'white', fontWeight: 600, px: 2.8, py: 0.9, borderRadius: '8px',
                textTransform: 'none', boxShadow: '0 2px 8px rgba(22, 163, 74, 0.25)',
                '&:hover': { bgcolor: '#15803D', boxShadow: '0 4px 12px rgba(22, 163, 74, 0.35)' }
              }}
            >
              Portal Login
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ── 1. Hero Banner (Matches VVSPL Product Green/Dark Header Banner) ── */}
      <Box id="hero" sx={{
        py: { xs: 8, md: 11 },
        background: 'linear-gradient(135deg, #0F172A 0%, #14532D 60%, #166534 100%)',
        color: 'white', position: 'relative', overflow: 'hidden'
      }}>
        {/* Subtle background glow */}
        <Box sx={{
          position: 'absolute', top: '-20%', right: '-10%', width: '600px', height: '600px',
          background: 'radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, rgba(0,0,0,0) 70%)',
          pointerEvents: 'none'
        }} />

        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                icon={<AutoAwesomeIcon sx={{ fontSize: '13px !important', color: '#4ADE80 !important' }} />}
                label="ENTERPRISE WORKFORCE PRESENTATION"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.12)', color: '#DCFCE7', border: '1px solid rgba(255, 255, 255, 0.2)',
                  fontWeight: 700, fontSize: 10.5, letterSpacing: 0.8, mb: 3
                }}
              />
              <Typography variant="h1" sx={{ fontSize: { xs: 36, md: 52 }, fontWeight: 800, lineHeight: 1.15, letterSpacing: -1, mb: 1.5 }}>
                Payroll HRMS
              </Typography>
              <Typography variant="h2" sx={{ fontSize: { xs: 18, md: 24 }, fontWeight: 700, color: '#86EFAC', mb: 2.5, letterSpacing: -0.3 }}>
                Smart Payroll & Workforce Management System
              </Typography>
              <Typography sx={{ fontSize: { xs: 15, md: 16.5 }, color: '#E2E8F0', lineHeight: 1.65, mb: 4, maxWidth: 540 }}>
                An end-to-end presentation of how our integrated payroll and HR engine automates salary structures, compliance calculations, employee profiles, and payment disbursements in one unified experience.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => scrollToSection('workflow')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: '#22C55E', color: 'white', fontWeight: 700, px: 3.5, py: 1.4, borderRadius: '8px',
                    textTransform: 'none', fontSize: 15, boxShadow: '0 4px 14px rgba(34, 197, 94, 0.4)',
                    '&:hover': { bgcolor: '#16A34A' }
                  }}
                >
                  Explore Payroll Process
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={onGoToLogin}
                  startIcon={<LoginIcon />}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.3)', color: 'white', fontWeight: 600, px: 3.5, py: 1.4, borderRadius: '8px',
                    textTransform: 'none', fontSize: 15,
                    '&:hover': { borderColor: '#86EFAC', bgcolor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  Login to Platform
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{
                position: 'relative', borderRadius: '16px', p: 1,
                bgcolor: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
              }}>
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/hero_illustration.png`}
                  alt="Payroll HRMS Hero Presentation"
                  sx={{
                    width: '100%', height: 'auto', borderRadius: '12px', display: 'block',
                    bgcolor: '#FFFFFF'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── 2. What is Payroll HRMS? (Clean Light Theme) ── */}
      <Box id="what-is-hrms" sx={{ py: { xs: 8, md: 11 }, bgcolor: '#FFFFFF', borderBottom: '1px solid #E2E8F0' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', maxWidth: 750, mx: 'auto', mb: 7 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 26, md: 36 }, fontWeight: 800, color: '#0F172A', mb: 1.5, letterSpacing: -0.5 }}>
              What is Payroll HRMS?
            </Typography>
            <Typography sx={{ fontSize: 16, color: '#64748B', lineHeight: 1.6 }}>
              Payroll HRMS is an all-in-one workforce and financial management platform designed to eliminate payroll errors, automate statutory compliance, and provide full visibility into employee compensation cycles.
            </Typography>
          </Box>

          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} lg={5}>
              <Box sx={{
                borderRadius: '16px', p: 1, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.04)'
              }}>
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/process_illustration.png`}
                  alt="What is Payroll HRMS"
                  sx={{ width: '100%', height: 'auto', borderRadius: '12px', display: 'block' }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} lg={7}>
              <Grid container spacing={2.5}>
                {[
                  { number: '01', title: 'Employee Management', desc: 'Centralized employee repository tracking assignments, designations, departments, and status changes.' },
                  { number: '02', title: 'Payroll Processing', desc: 'Automated gross-to-net salary calculations with multi-company and multi-period support.' },
                  { number: '03', title: 'Attendance & Leave', desc: 'Direct integration with daily attendance, shift logs, leave balances, and unpaid days.' },
                  { number: '04', title: 'Salary & Compliance', desc: 'Built-in statutory calculations for PF, ESI, Professional Tax, TDS, and Form 16 summaries.' }
                ].map((item, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Paper elevation={0} sx={{
                      p: 3, height: '100%', bgcolor: '#FFFFFF', borderRadius: '14px',
                      border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      transition: 'all 0.25s ease',
                      '&:hover': { borderColor: '#16A34A', transform: 'translateY(-3px)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }
                    }}>
                      <Chip label={item.number} size="small" sx={{ bgcolor: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0', fontWeight: 700, mb: 1.5 }} />
                      <Typography fontSize={17} fontWeight={700} color="#0F172A" sx={{ mb: 1 }}>
                        {item.title}
                      </Typography>
                      <Typography fontSize={14} color="#64748B" sx={{ lineHeight: 1.6 }}>
                        {item.desc}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── 3. Who Uses Payroll HRMS? (Clean Illustrated Grid / Infographic) ── */}
      <Box id="who-uses-hrms" sx={{ py: { xs: 8, md: 11 }, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} lg={6}>
              <Box sx={{ mb: 4 }}>
                <Chip label="TARGET AUDIENCE" size="small" sx={{ bgcolor: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0', fontWeight: 700, mb: 2 }} />
                <Typography variant="h2" sx={{ fontSize: { xs: 26, md: 36 }, fontWeight: 800, color: '#0F172A', mb: 2, letterSpacing: -0.5 }}>
                  Who Uses Payroll HRMS?
                </Typography>
                <Typography sx={{ fontSize: 16, color: '#64748B', lineHeight: 1.6, mb: 4 }}>
                  Our platform serves as a unified digital space, tailoring roles and tools specifically to the needs of every organizational stakeholder.
                </Typography>
              </Box>
              
              <Box sx={{
                borderRadius: '16px', p: 1, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
              }}>
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/users_illustration.png`}
                  alt="Who Uses Payroll HRMS"
                  sx={{ width: '100%', height: 'auto', borderRadius: '12px', display: 'block' }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Stack spacing={2.5}>
                {[
                  { title: 'HR Department', desc: 'Manages employee records, attendance, leave, and payroll operations.', icon: <PeopleIcon /> },
                  { title: 'Finance & Accounts', desc: 'Processes salaries, payments, taxes, and statutory compliance.', icon: <AttachMoneyIcon /> },
                  { title: 'Managers', desc: 'Monitor team information, attendance, and workforce reports.', icon: <VerifiedUserIcon /> },
                  { title: 'Employees', desc: 'Access payslips, salary details, leave balances, and payroll information.', icon: <DescriptionIcon /> },
                  { title: 'Business Owners', desc: 'Track payroll costs, workforce insights, and organizational performance.', icon: <TrendingUpIcon /> },
                  { title: 'Large Enterprises', desc: 'Manage multiple companies, departments, and thousands of employees from one centralized platform.', icon: <BusinessIcon /> }
                ].map((user, idx) => (
                  <Paper key={idx} elevation={0} sx={{
                    p: 2.5, bgcolor: '#FFFFFF', borderRadius: '12px', border: '1px solid #E2E8F0',
                    transition: 'all 0.25s ease', display: 'flex', gap: 2.5, alignItems: 'center',
                    '&:hover': { borderColor: '#16A34A', transform: 'translateX(6px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }
                  }}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: '10px', bgcolor: '#F0FDF4',
                      border: '1px solid #BBF7D0', color: '#16A34A', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      {user.icon}
                    </Box>
                    <Box>
                      <Typography fontSize={16} fontWeight={700} color="#0F172A" sx={{ mb: 0.5 }}>
                        {user.title}
                      </Typography>
                      <Typography fontSize={13.5} color="#64748B" sx={{ lineHeight: 1.5 }}>
                        {user.desc}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── 4. Why Payroll HRMS? (Before vs After) ── */}
      <Box id="why-hrms" sx={{ py: { xs: 8, md: 11 }, bgcolor: '#FFFFFF', borderBottom: '1px solid #E2E8F0' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', maxWidth: 700, mx: 'auto', mb: 7 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 26, md: 36 }, fontWeight: 800, color: '#0F172A', mb: 1.5, letterSpacing: -0.5 }}>
              Why Payroll HRMS?
            </Typography>
            <Typography sx={{ fontSize: 16, color: '#64748B' }}>
              See how moving from legacy paper-based workflows to an automated HRMS eliminates friction and guarantees compliance.
            </Typography>
          </Box>

          <Grid container spacing={3.5} alignItems="stretch">
            {/* Before Column */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                p: 3.5, height: '100%', bgcolor: '#FEF2F2', borderRadius: '16px',
                border: '1px solid #FCA5A5'
              }}>
                <Stack direction="row" alignItems="center" spacing={1.8} sx={{ mb: 3 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CancelOutlinedIcon sx={{ color: '#DC2626', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography fontSize={18} fontWeight={800} color="#991B1B">
                      Before Payroll HRMS
                    </Typography>
                    <Typography fontSize={12} color="#7F1D1D">Traditional Manual Payroll Challenges</Typography>
                  </Box>
                </Stack>

                <Stack spacing={2}>
                  {[
                    'Manual payroll calculations with high human error rates',
                    'Frequent payroll errors & manual salary correction disputes',
                    'Paper-based employee records scattered across files',
                    'Time-consuming salary processing taking days or weeks'
                  ].map((text, idx) => (
                    <Stack direction="row" spacing={1.5} alignItems="center" key={idx} sx={{ p: 1.8, borderRadius: '10px', bgcolor: '#FFFFFF', border: '1px solid #FEE2E2' }}>
                      <CancelOutlinedIcon sx={{ color: '#DC2626', fontSize: 18, flexShrink: 0 }} />
                      <Typography fontSize={14} color="#334155" fontWeight={500}>{text}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>

            {/* After Column */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                p: 3.5, height: '100%', bgcolor: '#F0FDF4', borderRadius: '16px',
                border: '1.5px solid #86EFAC', boxShadow: '0 4px 14px rgba(22, 163, 74, 0.08)'
              }}>
                <Stack direction="row" alignItems="center" spacing={1.8} sx={{ mb: 3 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircleOutlineIcon sx={{ color: '#16A34A', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography fontSize={18} fontWeight={800} color="#166534">
                      After Payroll HRMS
                    </Typography>
                    <Typography fontSize={12} color="#14532D">Modern Automated Presentation System</Typography>
                  </Box>
                </Stack>

                <Stack spacing={2}>
                  {[
                    'Automated payroll processing with instant zero-error execution',
                    'Accurate salary calculations with built-in statutory rules',
                    'Centralized employee management in a unified digital repository',
                    'Faster payroll with automated tax & compliance generation'
                  ].map((text, idx) => (
                    <Stack direction="row" spacing={1.5} alignItems="center" key={idx} sx={{ p: 1.8, borderRadius: '10px', bgcolor: '#FFFFFF', border: '1px solid #BBF7D0' }}>
                      <CheckCircleOutlineIcon sx={{ color: '#16A34A', fontSize: 18, flexShrink: 0 }} />
                      <Typography fontSize={14} color="#0F172A" fontWeight={600}>{text}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── 4. Payroll Workflow (14-Step Timeline in Light Theme) ── */}
      <Box id="workflow" sx={{ py: { xs: 8, md: 11 }, bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto', mb: 5 }}>
            <Chip label="INTERACTIVE PIPELINE" size="small" sx={{ bgcolor: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0', mb: 1.5, fontWeight: 700 }} />
            <Typography variant="h2" sx={{ fontSize: { xs: 26, md: 36 }, fontWeight: 800, color: '#0F172A', mb: 1.5, letterSpacing: -0.5 }}>
              The 14-Step Payroll Workflow
            </Typography>
            <Typography sx={{ fontSize: 16, color: '#64748B' }}>
              Follow the end-to-end lifecycle from initial company configuration to final bank disbursement.
            </Typography>
          </Box>

          {/* Step Selector Ribbon */}
          <Box sx={{ overflowX: 'auto', pb: 2, mb: 4, display: 'flex', gap: 1.2, scrollbarWidth: 'thin' }}>
            {workflowSteps.map((step, idx) => (
              <Box
                key={idx}
                onClick={() => setActiveStep(idx)}
                sx={{
                  flexShrink: 0, px: 2.2, py: 1.2, borderRadius: '10px', cursor: 'pointer',
                  bgcolor: activeStep === idx ? '#16A34A' : '#FFFFFF',
                  color: activeStep === idx ? 'white' : '#475569',
                  border: '1px solid', borderColor: activeStep === idx ? '#16A34A' : '#E2E8F0',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: activeStep === idx ? '#16A34A' : '#F1F5F9', color: activeStep === idx ? 'white' : '#0F172A' }
                }}
              >
                <Typography fontSize={11} fontWeight={800} opacity={0.85}>STEP {step.number}</Typography>
                <Typography fontSize={13} fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{step.title}</Typography>
              </Box>
            ))}
          </Box>

          {/* Active Step Presentation Card */}
          <Paper elevation={0} sx={{
            p: { xs: 3.5, md: 5 }, bgcolor: '#FFFFFF', borderRadius: '16px',
            border: '1.5px solid #BBF7D0', boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            position: 'relative', overflow: 'hidden'
          }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="center" justifyContent="space-between">
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Box sx={{
                    width: 50, height: 50, borderRadius: '12px', bgcolor: '#F0FDF4',
                    border: '1px solid #BBF7D0', color: '#16A34A', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {React.cloneElement(workflowSteps[activeStep].icon, { sx: { fontSize: 28 } })}
                  </Box>
                  <Box>
                    <Typography fontSize={12} fontWeight={800} color="#16A34A" letterSpacing={0.8}>
                      STEP {workflowSteps[activeStep].number} OF 14
                    </Typography>
                    <Typography variant="h3" fontSize={{ xs: 22, md: 28 }} fontWeight={800} color="#0F172A">
                      {workflowSteps[activeStep].title}
                    </Typography>
                  </Box>
                </Stack>

                <Typography fontSize={16} color="#475569" sx={{ lineHeight: 1.65, mb: 3.5 }}>
                  {workflowSteps[activeStep].desc}
                </Typography>

                <Stack direction="row" spacing={2}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep(prev => prev - 1)}
                    startIcon={<ChevronLeftIcon />}
                    sx={{ color: '#334155', borderColor: '#CBD5E1', textTransform: 'none', fontWeight: 600 }}
                    variant="outlined"
                  >
                    Previous Step
                  </Button>
                  <Button
                    disabled={activeStep === workflowSteps.length - 1}
                    onClick={() => setActiveStep(prev => prev + 1)}
                    endIcon={<ChevronRightIcon />}
                    sx={{ bgcolor: '#16A34A', color: 'white', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#15803D' } }}
                    variant="contained"
                  >
                    Next Step
                  </Button>
                </Stack>
              </Box>

              {/* Progress Indicator Graphic */}
              <Box sx={{
                width: { xs: '100%', md: 280 }, p: 3, borderRadius: '14px', bgcolor: '#F8FAFC',
                border: '1px solid #E2E8F0', textAlign: 'center'
              }}>
                <Typography fontSize={11} color="#64748B" fontWeight={700} letterSpacing={0.5} sx={{ mb: 0.5 }}>
                  WORKFLOW PROGRESS
                </Typography>
                <Typography fontSize={44} fontWeight={900} color="#16A34A" sx={{ lineHeight: 1 }}>
                  {Math.round(((activeStep + 1) / 14) * 100)}%
                </Typography>
                <Box sx={{ width: '100%', height: 6, bgcolor: '#E2E8F0', borderRadius: 3, my: 1.8, overflow: 'hidden' }}>
                  <Box sx={{ width: `${((activeStep + 1) / 14) * 100}%`, height: '100%', bgcolor: '#16A34A', transition: 'width 0.3s ease' }} />
                </Box>
                <Typography fontSize={12} color="#64748B" fontWeight={500}>
                  {activeStep + 1} of 14 steps selected
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Container>
      </Box>

      {/* ── 5. Core Modules (16 White Cards in Light Theme) ── */}
      <Box id="modules" sx={{ py: { xs: 8, md: 11 }, bgcolor: '#FFFFFF' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', maxWidth: 700, mx: 'auto', mb: 7 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 26, md: 36 }, fontWeight: 800, color: '#0F172A', mb: 1.5, letterSpacing: -0.5 }}>
              Core Modules
            </Typography>
            <Typography sx={{ fontSize: 16, color: '#64748B' }}>
              The 16 functional modules powering the entire Payroll HRMS ecosystem.
            </Typography>
          </Box>

          <Grid container spacing={2.5}>
            {coreModules.map((mod, idx) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                <Paper elevation={0} sx={{
                  p: 2.8, height: '100%', bgcolor: '#FFFFFF', borderRadius: '14px',
                  border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', transition: 'all 0.25s ease',
                  '&:hover': { borderColor: '#16A34A', transform: 'translateY(-3px)', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }
                }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '8px', bgcolor: '#F0FDF4',
                      border: '1px solid #BBF7D0', color: '#16A34A', display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      {React.cloneElement(mod.icon, { sx: { fontSize: 20 } })}
                    </Box>
                    <Typography fontSize={15.5} fontWeight={700} color="#0F172A">
                      {mod.name}
                    </Typography>
                  </Stack>
                  <Typography fontSize={13} color="#64748B" sx={{ lineHeight: 1.5 }}>
                    {mod.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── 6. Key Features (8 Cards in Light Theme) ── */}
      <Box id="features" sx={{ py: { xs: 8, md: 11 }, bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', maxWidth: 700, mx: 'auto', mb: 7 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 26, md: 36 }, fontWeight: 800, color: '#0F172A', mb: 1.5, letterSpacing: -0.5 }}>
              Key Features
            </Typography>
            <Typography sx={{ fontSize: 16, color: '#64748B' }}>
              8 essential capabilities delivering unmatched payroll efficiency and accuracy.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {keyFeatures.map((feat, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Paper elevation={0} sx={{
                  p: 3, height: '100%', bgcolor: '#FFFFFF', borderRadius: '14px',
                  border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', transition: 'all 0.25s ease',
                  '&:hover': { borderColor: '#16A34A', transform: 'translateY(-3px)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }
                }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '10px', bgcolor: '#F0FDF4',
                    border: '1px solid #BBF7D0', color: '#16A34A', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2
                  }}>
                    {React.cloneElement(feat.icon, { sx: { fontSize: 24 } })}
                  </Box>
                  <Typography fontSize={16.5} fontWeight={700} color="#0F172A" sx={{ mb: 1 }}>
                    {feat.title}
                  </Typography>
                  <Typography fontSize={13.5} color="#64748B" sx={{ lineHeight: 1.55 }}>
                    {feat.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── 7. Business Benefits ── */}
      <Box id="benefits" sx={{ py: { xs: 8, md: 11 }, bgcolor: '#FFFFFF' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', maxWidth: 700, mx: 'auto', mb: 7 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 26, md: 36 }, fontWeight: 800, color: '#0F172A', mb: 1.5, letterSpacing: -0.5 }}>
              Business Benefits
            </Typography>
            <Typography sx={{ fontSize: 16, color: '#64748B' }}>
              The operational transformation delivered across your organization.
            </Typography>
          </Box>

          <Grid container spacing={3.5}>
            {/* Column 1: Before */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3.5, bgcolor: '#FEF2F2', borderRadius: '16px', border: '1px solid #FCA5A5' }}>
                <Typography fontSize={17} fontWeight={800} color="#991B1B" sx={{ mb: 2.5 }}>
                  Before Payroll HRMS
                </Typography>
                <Stack spacing={1.8}>
                  {[
                    'Manual salary calculations',
                    'Payroll processing delays',
                    'Attendance calculation errors',
                    'Paper-based HR records'
                  ].map((item, idx) => (
                    <Stack direction="row" spacing={1.5} alignItems="center" key={idx}>
                      <CancelOutlinedIcon sx={{ color: '#DC2626', fontSize: 18 }} />
                      <Typography fontSize={14.5} color="#334155" fontWeight={500}>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>

            {/* Column 2: After */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3.5, bgcolor: '#F0FDF4', borderRadius: '16px', border: '1.5px solid #86EFAC' }}>
                <Typography fontSize={17} fontWeight={800} color="#166534" sx={{ mb: 2.5 }}>
                  After Payroll HRMS
                </Typography>
                <Stack spacing={1.8}>
                  {[
                    'Automated payroll',
                    'Faster salary processing',
                    'Accurate payroll calculations',
                    'Centralized employee data'
                  ].map((item, idx) => (
                    <Stack direction="row" spacing={1.5} alignItems="center" key={idx}>
                      <CheckCircleOutlineIcon sx={{ color: '#16A34A', fontSize: 18 }} />
                      <Typography fontSize={14.5} color="#0F172A" fontWeight={600}>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>



      {/* ── 9. Project Highlights (Infographic Tiles with Accent Pink Card) ── */}
      <Box id="highlights" sx={{ py: { xs: 8, md: 11 }, bgcolor: '#F8FAFC' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', maxWidth: 700, mx: 'auto', mb: 7 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 26, md: 36 }, fontWeight: 800, color: '#0F172A', mb: 1.5, letterSpacing: -0.5 }}>
              Project Highlights
            </Typography>
            <Typography sx={{ fontSize: 16, color: '#64748B' }}>
              System capability overview presented as key performance highlights.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {highlights.map((h, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Paper elevation={0} sx={{
                  p: 3.5, height: '100%', textAlign: 'center', borderRadius: '16px',
                  bgcolor: h.highlight ? '#FDF2F8' : '#FFFFFF',
                  border: h.highlight ? '1.5px solid #F472B6' : '1px solid #E2E8F0',
                  boxShadow: h.highlight ? '0 4px 14px rgba(236, 72, 153, 0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
                  transition: 'all 0.25s ease',
                  '&:hover': { borderColor: h.highlight ? '#EC4899' : '#16A34A', transform: 'translateY(-3px)' }
                }}>
                  <Typography fontSize={12} fontWeight={800} color={h.highlight ? '#DB2777' : '#16A34A'} letterSpacing={0.8} sx={{ mb: 1, textTransform: 'uppercase' }}>
                    {h.title}
                  </Typography>
                  <Typography fontSize={24} fontWeight={900} color={h.highlight ? '#BE185D' : '#0F172A'} sx={{ mb: 1 }}>
                    {h.value}
                  </Typography>
                  <Typography fontSize={13.5} color={h.highlight ? '#9D174D' : '#64748B'}>
                    {h.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── 10. Light Footer ── */}
      <Box sx={{ py: 5, bgcolor: '#FFFFFF', borderTop: '1px solid #E2E8F0' }}>
        <Container maxWidth="xl">
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" justifyContent="space-between" spacing={3}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{
                width: 34, height: 34, borderRadius: '8px',
                background: 'linear-gradient(135deg, #16A34A, #22C55E)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Typography fontWeight={900} fontSize={17} color="white">V</Typography>
              </Box>
              <Box>
                <Typography fontSize={15} fontWeight={800} color="#0F172A">
                  Payroll HRMS
                </Typography>
                <Typography fontSize={11} color="#64748B">
                  Smart Payroll & Workforce Management System
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={3} alignItems="center">
              <Button onClick={onGoToLogin} startIcon={<LoginIcon />} sx={{ color: '#16A34A', fontWeight: 600, textTransform: 'none' }}>
                Login
              </Button>
              <Typography fontSize={13} color="#64748B">
                © 2026 VVSPL HRMS. All rights reserved.
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>

    </Box>
  );
}
